export type SessionStorageKeyType = typeof SessionStorageKeys[keyof typeof SessionStorageKeys];

/**
 * Houses all keys used in session storage
 */
export const SessionStorageKeys = {
    /**
     * String - Random string used in OAuth flow
     */
    CodeVerifier: 'code_verifier',
    /**
     * String - Authentication token for API calls
     */
    Token: 'token',
    /**
     * LoginTokenTypes string - Denotes what JWT login token is used (dlrSecured / GoMoto)
     */
    TokenType: 'token_type',
    /**
     * String - Login session guid for the user
     */
    LoginSession: 'login_session',
    /**
     * String - Token used to generate a new authentication token when the old one expires
     */
    RefreshToken: 'refresh_token',
    /**
     * String - Username used for JWT refresh logic.
     */
    Username: 'username',
    /**
     * Date string - Start date for dashboard data
     */
    StartDate: 'startDate',
    /**
     * ViewRange string - Date range view mode for dashboard data
     */
    ViewMode: 'viewMode',
    /**
     * String[] - Dealer keys selected for dashboard data
     */
    DealersSelected: 'storesSelected',
    /**
     * String[] - DMSs selected for dashboard data
     */
    DmsSelected: 'dmsSelected',
    /**
     * String[] - Dealer group keys selected for dashboard data
     */
    GroupsSelected: 'groupsSelected',
    /**
     * String[] - Regions selected for dashboard data
     */
    RegionsSelected: 'regionsSelected',
    /**
     * String[]  - Brands selected for dashboard data
     */
    BrandsSelected: 'brandsSelected',
    /**
     * Boolean - whether the dealer overview is displayed for summary dashboard
     */
    DlrOverview: 'dlrOverview',
    /**
     * ViewRange string - Date range view mode for overview compare screen
     */
    CompareViewMode: 'selectViewRange',
    /**
     * Date string - Date value for overview compare screen
     */
    CompareDate: 'compareDate',
    /**
     * CompareType - Compare type for overview compare screen
     */
    CompareType: 'compareType',
    /**
     * String[] - Dealer keys selected for compare screen
     */
    CompareDealersSelected: 'comparestoresSelected',
    /**
     * String[] - Brands selected for compare screen
     */
    CompareBrandsSelected: 'comparebrandsSelected',
    /**
     * String[] - Regions selected for compare screen
     */
    CompareRegionsSelected: 'compareregionsSelected',
    /**
     * String[] - DMSs selected for compare screen
     */
    CompareDmsSelected: 'comparedmsSelected',
    /**
     * CheckInType[] (string[]) - Check In types selected for activity screen
     */
    ActivityCheckInTypesSelected: 'check-in typesSelected',
    /**
     * PaymentStatusType[] (string[]) - Payment status types selected for activity screen
     */
    ActivityPaymentStatusTypesSelected: 'payment statusesSelected',
    /**
     * string[] - Advisors selected for activity screen
     */
    ActivityAdvisorsSelected: 'advisorsSelected',

    // #region Kiosk Filters
    /**
     * string[] - CIF Accounts filter options selected for Support Kiosk screen
     */
    KioskCIFAccounts: 'kiosk_cifAccounts',
    /**
     * string[] - Kiosk Names filter options selected for Support Kiosk screen
     */
    KioskNames: 'kiosk_names',
    /**
     * string[] - Kiosk Names filter options selected for Support Kiosk screen
     */
    KioskTypes: 'kiosk_types',
    /**
     * string[] - Machine IDs filter options selected for Support Kiosk screen
     */
    KioskMachineIds: 'kiosk_machineIds',
    /**
     * string[] - Kiosk Serial Numbers filter options selected for Support Kiosk screen
     */
    KioskSerialNumbers: 'kiosk_serialNumbers',
    /**
     * string[] - DMS Types filter options selected for Support Kiosk screen
     */
    KioskDMSTypes: 'kiosk_dmsTypes',
    /**
     * string - Kiosk status filter options selected for the Support Kiosk Configuration (previously Kiosk) screen.
     */
    KioskStatus: 'kiosk_status',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Kiosk screen
     */
    KioskColumnSort: 'kiosk_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Kiosk screen
     */
    KioskRowsPerPage: 'kiosk_rowsPerPage',
    // #endregion

    // #region Health Filters
    /**
     * Date | null - Health Check Start Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    HealthCheckStartDate: 'healthCheck_startDate',
    /**
     * Date | null - Health Check End Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    HealthCheckEndDate: 'healthCheck_endDate',
    /**
     * string[] - Kiosk Names filter options selected for Support Health Check screen
     */
    HealthCheckNames: 'healthCheck_names',
    /**
     * string[] - Machine IDs filter options selected for Support Health Check screen
     */
    HealthCheckMachineIds: 'healthCheck_machineIds',
    /**
     * string[] - Kiosk Status Codes filter options selected for Support Health Check screen
     */
    HealthCheckStatusCodes: 'healthCheck_statusCodes',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Health Check screen
     */
    HealthCheckColumnSort: 'healthCheck_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Health Check screen
     */
    HealthCheckRowsPerPage: 'healthCheck_rowsPerPage',
    // #endregion

    // #region Response Filters
    /**
     * string[] - The response log ids to filter to in the Support Response Logs screen
     */
    ResponseLogIds: 'responseLog_ids',
    /**
     * string[] - The response log ids to filter to in the Support Response Logs screen
     */
    ResponseLogVins: 'responseLog_vins',
    /**
     * Date | null - Response Log Start Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    ResponseLogStartDate: 'responseLog_startDate',
    /**
     * Date | null - Response Log End Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    ResponseLogEndDate: 'responseLog_endDate',
    /**
     * string[] - Response Log Provider IDs filter options selected for Support Reponse Logs screen
     */
    ResponseLogProviders: 'responseLog_providers',
    /**
     * string[] - Response Log Dealer IDs filter options selected for Support Reponse Logs screen
     */
    ResponseLogDealers: 'responseLog_dealers',
    /**
     * string[] - Response Log Transaction Type IDs filter options selected for Support Reponse Logs screen
     */
    ResponseLogTransactionTypes: 'responseLog_transactionTypes',
    /**
     * string[] - Response Log Status Codes filter options selected for Support Reponse Logs screen
     */
    ResponseLogStatusCodes: 'responseLog_statusCodes',
    /**
     * string[] - Response Log Result Codes filter options selected for Support Reponse Logs screen
     */
    ResponseLogResultCodes: 'responseLog_resultCodes',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Response Logs screen
     */
    ResponseLogColumnSort: 'responseLog_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Response Log screen
     */
    ResponseLogRowsPerPage: 'responseLog_rowsPerPage',
    // #endregion

    // #region User Filters
    /**
     * string - User Management name filter for the Support User Management screen
     */
    UserManagementName: 'userManagement_name',
    /**
     * string[] - User Management username drop down filter for the Support User Management screen
     */
    UserManagementUsername: 'userManagement_username',
    /**
     * string[] - User Management role name filter for the Support User Managment screen
     */
    UserManagementRoleNames: 'userManagement_roleNames',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support User Management screen
     */
    UserManagementColumnSort: 'userManagement_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support User Management screen
     */
    UserManagementRowsPerPage: 'userManagement_rowsPerPage',
    // #endregion

    // #region Dealer Filters
    /**
     * string[] - CIF Accounts filter options selected for Support Dealer Settings screen
     */
    DealerCIFAccounts: 'dealer_cifAccounts',
    /**
     * string[] - Dealer Names filter options selected for Support Dealer Settings screen
     */
    DealerNames: 'dealer_names',
    /**
     * string[] - DMS Types filter options selected for Support Dealer Settings screen
     */
    DealerDMSTypes: 'dealer_dmsTypes',
    /**
     * string[] - Timezones filter options selected for Support Dealer Settings screen
     */
    DealerTimezones: 'dealer_timezones',
    /**
     * string[] - Dealer Statuses filter options selected for Support Dealer Settings screen
     */
    DealerStatuses: 'dealer_statuses',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Dealer Settings screen
     */
    DealerColumnSort: 'dealer_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Dealer Settings screen
     */
    DealerRowsPerPage: 'dealer_rowsPerPage',
    // #endregion

    // #region Dealer Group Filters
    /**
     * string[] - Dealer group names filter options selected for Support Dealer Groups screen
     */
    DealerGroupNames: 'dealerGroup_names',
    /**
     * string[] - Dealer Group dealer name filter options selected for Support Dealer Groups screen
     */
    DealerGroupsDealers: 'dealerGroup_dealerNames',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Dealer Groups screen
     */
    DealerGroupsColumnSort: 'dealerGroup_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Dealer Groups screen
     */
    DealerGroupsRowsPerPage: 'dealerGroup_rowsPerPage',
    // #endregion

    // #region Kiosk Banner Filters
    /**
     * string[] - Kiosk Banner kiosk names filter options selected for Support Kiosk Banner screen
     */
    KioskBannersKioskNames: 'kioskBanner_kioskNames',
    /**
     * string[] - Kiosk Banner dealer name filter options selected for Support Kiosk Banner screen
     */
    KioskBannersDealerNames: 'kioskBanner_dealerNames',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Kiosk Banner screen
     */
    KioskBannerColumnSort: 'kioskBanner_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Kiosk Banner screen
     */
    KioskBannerRowsPerPage: 'kioskBanner_rowsPerPage',
    // #endregion

    // #region Fortellis Filters
    /**
     * string[] - Fortellis Swap Tables dealer names filter options selected for Support Fortellis Swap Tables screen
     */
    FortellisSwapTablesDealerNames: 'fortellisSwapTable_dealerNames',
    /**
     * string[] - Fortellis Swap Tables op code names filter options selected for Support Fortellis Swap Tables screen
     */
    FortellisSwapTablesOpCodeNames: 'fortellisSwapTable_opCodeNames',
    /**
     * string[] - Fortellis Swap Tables od code numeric ids filter options selected for Support Fortellis Swap Tables screen
     */
    FortellisSwapTablesOpCodes: 'fortellisSwapTable_opCodes',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Fortellis Swap Tables screen
     */
    FortellisSwapTableColumnSort: 'fortellisSwapTable_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Fortellis Swap Tables screen
     */
    FortellisSwapTableRowsPerPage: 'fortellisSwapTable_rowsPerPage',
    // #endregion

    // #region SMS Whitelist Filters
    /**
     * string - SMS Whitelist text entry field for a single name or username filter for the Support SMS Whitelist screen
     */
    SmsWhitelistNameOrUsername: 'smsWhitelist_nameOrUsername',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support SMS Whitelist screen
     */
    SmsWhitelistColumnSort: 'smsWhitelist_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support SMS Whitelist screen
     */
    SmsWhitelistRowsPerPage: 'smsWhitelist_rowsPerPage',
    // #endregion

    // #region Hunter Vehicles Filters
    /**
     * string[] - The hunter vehicle ids to filter to in the Support Hunter Vehicles screen
     */
    HunterVehiclesIds: 'hunterVehicles_ids',
    /**
     * string[] - The hunter vehicle vins to filter to in the Support Hunter Vehicles screen
     */
    HunterVehiclesVins: 'hunterVehicles_vins',
    /**
     * Date | null - Hunter Vehicles Start Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    HunterVehiclesStartDate: 'hunterVehicles_startDate',
    /**
     * Date | null - Hunter Vehicles End Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    HunterVehiclesEndDate: 'hunterVehicles_endDate',
    /**
     * string[] - Hunter Vehicles Dealer IDs filter options selected for Support Hunter Vehicles screen
     */
    HunterVehiclesDealers: 'hunterVehicles_dealers',
    /**
     * string - Hunter Vehicles Wheel Alignment filter options selected for Support Hunter Vehicles screen
     */
    HunterVehiclesWheelAlignment: 'hunterVehicles_wheelAlignment',
    /**
     * string - Hunter Vehicles Tire Check filter options selected for Support Hunter Vehicles screen
     */
    HunterVehiclesTireCheck: 'hunterVehicles_tireCheck',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Hunter Vehicles screen
     */
    HunterVehiclesColumnSort: 'hunterVehicles_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Hunter Vehicles screen
     */
    HunterVehiclesRowsPerPage: 'hunterVehicles_rowsPerPage',
    // #endregion

    // #region Notification Delivery Status Report
    /**
     * Date | null - Notification Delivery Status Report Start Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    NotificationDeliveryStatusReportStartDate: 'notificationDeliveryStatusReport_startDate',
    /**
     * Date | null - Notification Delivery Status Report End Date filter. This isn't selected directly, but can be altered via the day select filter.
     */
    NotificationDeliveryStatusReportEndDate: 'notificationDeliveryStatusReport_endDate',
    /**
     * number[] - Dealer ID filter for the Notification Delivery Status Report screen.
     */
    NotificationDeliveryStatusReportDealerIds: 'notificationDeliveryStatusReport_dealerIds',
    /**
     * NotificationDeliveryStatusViewAs - View mode filter (counts vs percentages) for the Notification Delivery Status Report screen.
     */
    NotificationDeliveryStatusReportViewAs: 'notificationDeliveryStatusReport_viewAs',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Notification Delivery Status Report screen
     */
    NotificationDeliveryStatusReportColumnSort: 'notificationDeliveryStatusReport_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Notification Delivery Status Report screen
     */
    NotificationDeliveryStatusReportRowsPerPage: 'notificationDeliveryStatusReport_rowsPerPage',
    // #endregion

    // #region Services
    /**
     * string[] - Services Dealer Names filter for the Services screen.
     */
    ServicesDealerNames: 'services_dealerNames',
    /**
     * string - Services Name or Opcode free-text filter for the Services screen.
     */
    ServicesNameOrOpcode: 'services_nameOrOpcode',
    /**
     * string[] - Services Kiosk Names filter for the Services screen.
     */
    ServicesKioskNames: 'services_kioskNames',
    /**
     * number[] - Services Service Types ({@link ServiceTypeValues}) filter for the Services screen.
     */
    ServicesServiceTypes: 'services_serviceTypes',
    /**
     * number - Services Unassigned filter ({@link UnassignedServicesFilterValues}) for the Services screen.
     */
    ServicesUnassignedFilter: 'services_unassignedFilter',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Services screen
     */
    ServicesColumnSort: 'services_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Services screen
     */
    ServicesRowsPerPage: 'services_rowsPerPage',
    // #endregion

    // #region Check-In Completion (KPI report)
    /**
     * KpiReportViewTypeValues - view filter option selected for the Support Check-In Completion screen
     */
    CheckInCompletionView: 'checkInCompletion_view',
    /**
     * KpiReportMetricTypeValues - metric filter option selected for the Support Check-In Completion screen
     */
    CheckInCompletionMetric: 'checkInCompletion_metric',
    /**
     * Date | null - Check-In Completion Start Date filter. This isn't selected directly, but can be altered via the date range filter.
     */
    CheckInCompletionStartDate: 'checkInCompletion_startDate',
    /**
     * Date | null - Check-In Completion End Date filter. This isn't selected directly, but can be altered via the date range filter.
     */
    CheckInCompletionEndDate: 'checkInCompletion_endDate',
    /**
     * DealerStatus - dealer status filter option selected for the Support Check-In Completion screen
     */
    CheckInCompletionDealerStatus: 'checkInCompletion_dealerStatus',
    /**
     * string | null - CIF number filter for the Support Check-In Completion screen
     */
    CheckInCompletionCifNumber: 'checkInCompletion_cifNumber',
    /**
     * string[] - dealer names filter options selected for the Support Check-In Completion screen
     */
    CheckInCompletionDealerNames: 'checkInCompletion_dealerNames',
    /**
     * string[] - kiosk names filter options selected for the Support Check-In Completion screen
     */
    CheckInCompletionKioskNames: 'checkInCompletion_kioskNames',
    /**
     * RegionValues[] - regions filter options selected for the Support Check-In Completion screen
     */
    CheckInCompletionRegions: 'checkInCompletion_regions',
    /**
     * string[] - DMS abbreviations filter options selected for the Support Check-In Completion screen
     */
    CheckInCompletionDmsAbbreviations: 'checkInCompletion_dmsAbbreviations',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Check-In Completion screen
     */
    CheckInCompletionColumnSort: 'checkInCompletion_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Check-In Completion screen
     */
    CheckInCompletionRowsPerPage: 'checkInCompletion_rowsPerPage',
    // #endregion

    // #region Check-In Selections (KPI report)
    /**
     * KpiReportViewTypeValues - view filter option selected for the Support Check-In Selections screen
     */
    CheckInSelectionsView: 'checkInSelections_view',
    /**
     * KpiReportMetricTypeValues - metric filter option selected for the Support Check-In Selections screen
     */
    CheckInSelectionsMetric: 'checkInSelections_metric',
    /**
     * Date | null - Check-In Selections Start Date filter. This isn't selected directly, but can be altered via the date range filter.
     */
    CheckInSelectionsStartDate: 'checkInSelections_startDate',
    /**
     * Date | null - Check-In Selections End Date filter. This isn't selected directly, but can be altered via the date range filter.
     */
    CheckInSelectionsEndDate: 'checkInSelections_endDate',
    /**
     * DealerStatus - dealer status filter option selected for the Support Check-In Selections screen
     */
    CheckInSelectionsDealerStatus: 'checkInSelections_dealerStatus',
    /**
     * string | null - CIF number filter for the Support Check-In Selections screen
     */
    CheckInSelectionsCifNumber: 'checkInSelections_cifNumber',
    /**
     * string[] - dealer names filter options selected for the Support Check-In Selections screen
     */
    CheckInSelectionsDealerNames: 'checkInSelections_dealerNames',
    /**
     * string[] - kiosk names filter options selected for the Support Check-In Selections screen
     */
    CheckInSelectionsKioskNames: 'checkInSelections_kioskNames',
    /**
     * RegionValues[] - regions filter options selected for the Support Check-In Selections screen
     */
    CheckInSelectionsRegions: 'checkInSelections_regions',
    /**
     * string[] - DMS abbreviations filter options selected for the Support Check-In Selections screen
     */
    CheckInSelectionsDmsAbbreviations: 'checkInSelections_dmsAbbreviations',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Check-In Selections screen
     */
    CheckInSelectionsColumnSort: 'checkInSelections_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Check-In Selections screen
     */
    CheckInSelectionsRowsPerPage: 'checkInSelections_rowsPerPage',
    // #endregion

    // #region Pickup (KPI report)
    /**
     * KpiReportViewTypeValues - view filter option selected for the Support Pickup KPI report screen. Prefixed `kpiPickup` to
     * avoid colliding with any other pickup-related storage keys.
     */
    KpiPickupView: 'kpiPickup_view',
    /**
     * KpiReportMetricTypeValues - metric filter option selected for the Support Pickup KPI report screen
     */
    KpiPickupMetric: 'kpiPickup_metric',
    /**
     * Date | null - Pickup KPI report Start Date filter. This isn't selected directly, but can be altered via the date range filter.
     */
    KpiPickupStartDate: 'kpiPickup_startDate',
    /**
     * Date | null - Pickup KPI report End Date filter. This isn't selected directly, but can be altered via the date range filter.
     */
    KpiPickupEndDate: 'kpiPickup_endDate',
    /**
     * DealerStatus - dealer status filter option selected for the Support Pickup KPI report screen
     */
    KpiPickupDealerStatus: 'kpiPickup_dealerStatus',
    /**
     * string | null - CIF number filter for the Support Pickup KPI report screen
     */
    KpiPickupCifNumber: 'kpiPickup_cifNumber',
    /**
     * string[] - dealer names filter options selected for the Support Pickup KPI report screen
     */
    KpiPickupDealerNames: 'kpiPickup_dealerNames',
    /**
     * string[] - kiosk names filter options selected for the Support Pickup KPI report screen
     */
    KpiPickupKioskNames: 'kpiPickup_kioskNames',
    /**
     * RegionValues[] - regions filter options selected for the Support Pickup KPI report screen
     */
    KpiPickupRegions: 'kpiPickup_regions',
    /**
     * string[] - DMS abbreviations filter options selected for the Support Pickup KPI report screen
     */
    KpiPickupDmsAbbreviations: 'kpiPickup_dmsAbbreviations',
    /**
     * SortColumn[] - Array of SortColumn enum values to indicate how each column is sorted on the Support Pickup KPI report screen
     */
    KpiPickupColumnSort: 'kpiPickup_columnSort',
    /**
     * number - A numeric value to indicate how many rows should be loaded when the page is returned to on the Support Pickup KPI report screen
     */
    KpiPickupRowsPerPage: 'kpiPickup_rowsPerPage',
    // #endregion
} as const;

/**
 * Houses all keys used in local storage
 */
export const LocalStorageKeys = {
    /**
     * String - The user's chosen locale from the login screen.
     */
    BrowserLang: 'browserLang',
} as const;
